import { useEffect, useRef, useState } from 'react';
import { Brain, Loader2 } from 'lucide-react';
import { ExpandableSection } from '@/components/expandable-section';

interface ThinkingBlockProps {
  content: string;
  duration?: number; // 思考耗时（秒）
  defaultExpanded?: boolean;
  /** true = AI 正在思考中（加载态：spinner + 秒数自计）；false = 已思考完成 */
  thinking?: boolean;
  /** 加载态文案，默认「AI 正在思考…」；GDD 场景传入「正在生成游戏设计文档」 */
  thinkingLabel?: string;
}

export function ThinkingBlock({ content, duration, defaultExpanded = false, thinking = false, thinkingLabel }: ThinkingBlockProps) {
  // 进行中自计秒数；thinking 由 true → false 时定格为最终耗时
  // （后端 thinking 事件 duration 恒为 0，前端必须自计，否则「思考用时」永远为 0）
  const [elapsed, setElapsed] = useState(0);
  const [finalDuration, setFinalDuration] = useState<number | undefined>(undefined);
  const wasThinking = useRef(thinking);

  useEffect(() => {
    if (thinking) {
      wasThinking.current = true;
      const timer = setInterval(() => setElapsed((s) => s + 1), 1000);
      return () => clearInterval(timer);
    }
    if (wasThinking.current) {
      wasThinking.current = false;
      setFinalDuration((prev) => prev ?? elapsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thinking]);

  if (thinking) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-neon-cyan/25 bg-neon-cyan/5 px-3 py-2 text-xs text-neon-cyan/90">
        <Loader2 size={14} className="shrink-0 animate-spin" />
        <span className="flex-1 truncate">{thinkingLabel ?? 'AI 正在思考…'}</span>
        <span className="shrink-0 font-mono text-[11px] text-neon-cyan/70">{elapsed}s</span>
      </div>
    );
  }

  return (
    <ExpandableSection
      title="已思考"
      icon={<Brain size={14} />}
      defaultExpanded={defaultExpanded}
      variant="subtle"
      showDuration={finalDuration ?? (duration && duration > 0 ? duration : undefined)}
    >
      <p className="whitespace-pre-wrap leading-relaxed">{content}</p>
    </ExpandableSection>
  );
}
